<template>
  <div id="sliders-container" class="home-hero">
    <div id="layerslider-container">
      <div
        v-if="loading"
        class="home-hero-slider home-hero-slider--loading"
        role="region"
        aria-label="Homepage banner slideshow"
      >
        <div class="home-hero-slider__viewport">
          <div class="home-hero-slider__placeholder">{{ $t('slider.loading') }}</div>
        </div>
      </div>

      <div
        v-else-if="slides.length"
        class="home-hero-slider"
        role="region"
        aria-label="Homepage banner slideshow"
        @mouseenter="paused = true"
        @mouseleave="paused = false"
      >
        <div class="home-hero-slider__viewport">
          <div
            v-for="(slide, index) in slides"
            :key="slide.id"
            class="home-hero-slider__slide"
            :class="{ 'is-active': index === currentIndex }"
            :aria-hidden="index !== currentIndex"
          >
            <img
              :src="slide.imageUrl"
              alt=""
              loading="eager"
              decoding="async"
            />
          </div>
        </div>

        <button
          v-if="slides.length > 1"
          type="button"
          class="home-hero-slider__arrow home-hero-slider__arrow--prev"
          aria-label="Previous slide"
          @click="prev"
        />

        <button
          v-if="slides.length > 1"
          type="button"
          class="home-hero-slider__arrow home-hero-slider__arrow--next"
          aria-label="Next slide"
          @click="next"
        />

        <div v-if="slides.length > 1" class="home-hero-slider__bottom">
          <button
            type="button"
            class="home-hero-slider__play"
            :class="{ 'is-paused': paused }"
            :aria-label="paused ? 'Start slideshow' : 'Pause slideshow'"
            @click="togglePause"
          />
          <div class="home-hero-slider__dots" role="tablist" aria-label="Choose slide">
            <button
              v-for="(slide, index) in slides"
              :key="'dot-' + slide.id"
              type="button"
              role="tab"
              class="home-hero-slider__dot"
              :class="{ 'is-active': index === currentIndex }"
              :aria-selected="index === currentIndex"
              :aria-label="'Go to slide ' + (index + 1)"
              @click="goTo(index)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchPublicBanners, mapStorefrontBanner } from '@/api/banners'

const INTERVAL_MS = 6000

export default {
  name: 'HomeHeroSlider',
  data() {
    return {
      slides: [],
      loading: true,
      currentIndex: 0,
      paused: false,
      timer: null
    }
  },
  async mounted() {
    await this.loadBanners()
    if (this.slides.length > 1) {
      this.startAutoplay()
    }
  },
  beforeUnmount() {
    this.stopAutoplay()
  },
  methods: {
    async loadBanners() {
      this.loading = true
      try {
        const res = await fetchPublicBanners()
        const items = res.data?.items || []
        this.slides = items.map(mapStorefrontBanner).filter((s) => s.imageUrl)
        this.currentIndex = 0
      } catch {
        this.slides = []
      } finally {
        this.loading = false
      }
    },
    startAutoplay() {
      this.stopAutoplay()
      this.timer = setInterval(() => {
        if (!this.paused && this.slides.length > 1) this.next()
      }, INTERVAL_MS)
    },
    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    goTo(index) {
      this.currentIndex = index
    },
    next() {
      if (!this.slides.length) return
      this.currentIndex = (this.currentIndex + 1) % this.slides.length
    },
    prev() {
      if (!this.slides.length) return
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length
    },
    togglePause() {
      this.paused = !this.paused
    }
  }
}
</script>

<style scoped>
#layerslider-container {
  max-width: 1920px;
  margin: 0 auto;
}

.home-hero-slider {
  position: relative;
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background: #111;
}

.home-hero-slider__viewport {
  position: relative;
  width: 100%;
  height: 550px;
  overflow: hidden;
}

.home-hero-slider__slide {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.8s ease;
  pointer-events: none;
}

.home-hero-slider__slide.is-active {
  opacity: 1;
  pointer-events: auto;
  z-index: 1;
}

.home-hero-slider__slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.home-hero-slider__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 550px;
  background: linear-gradient(135deg, #2a2b2c 0%, #4a4a4a 100%);
  color: rgba(255, 255, 255, 0.35);
  font-size: 20px;
  letter-spacing: 0.05em;
}

.home-hero-slider--loading .home-hero-slider__viewport {
  min-height: 550px;
}

.home-hero-slider__arrow {
  position: absolute;
  top: 50%;
  z-index: 10;
  width: 44px;
  height: 44px;
  margin-top: -22px;
  padding: 0;
  border: none;
  background: rgba(0, 0, 0, 0.35);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.25s ease, background 0.25s ease;
}

.home-hero-slider:hover .home-hero-slider__arrow {
  opacity: 1;
}

.home-hero-slider__arrow:hover {
  background: rgba(0, 0, 0, 0.55);
}

.home-hero-slider__arrow--prev {
  left: 16px;
}

.home-hero-slider__arrow--next {
  right: 16px;
}

.home-hero-slider__arrow::after {
  content: '';
  display: block;
  width: 12px;
  height: 12px;
  margin: 0 auto;
  border-top: 2px solid #fff;
  border-right: 2px solid #fff;
}

.home-hero-slider__arrow--prev::after {
  transform: rotate(-135deg);
  margin-left: 18px;
}

.home-hero-slider__arrow--next::after {
  transform: rotate(45deg);
  margin-right: 18px;
}

.home-hero-slider__bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 18px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.home-hero-slider__play {
  width: 14px;
  height: 14px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.85;
}

.home-hero-slider__play::before {
  content: '';
  display: block;
  width: 4px;
  height: 12px;
  margin: 0 auto;
  box-shadow: 6px 0 0 #fff;
  background: #fff;
}

.home-hero-slider__play.is-paused::before {
  width: 0;
  height: 0;
  box-shadow: none;
  background: transparent;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 10px solid #fff;
  margin-left: 3px;
}

.home-hero-slider__dots {
  display: flex;
  align-items: center;
  gap: 8px;
}

.home-hero-slider__dot {
  width: 12px;
  height: 12px;
  padding: 0;
  border: 2px solid rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.home-hero-slider__dot.is-active,
.home-hero-slider__dot:hover {
  background: #fff;
  border-color: #fff;
}

@media (max-width: 800px) {
  .home-hero-slider__viewport,
  .home-hero-slider__placeholder {
    height: 220px;
    min-height: 220px;
  }

  .home-hero-slider--loading .home-hero-slider__viewport {
    min-height: 220px;
  }

  .home-hero-slider__arrow {
    opacity: 1;
    width: 36px;
    height: 36px;
    margin-top: -18px;
  }
}
</style>
